import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { IconButton, Typography, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AjustesChangeScreen from "./AjustesChangeScreen";
import "./Ajustes.css";

const EmailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.8809 6.77222L12.0137 11.7833C11.7468 11.9384 11.4435 12.0201 11.1348 12.0201C10.8261 12.0201 10.5228 11.9384 10.2559 11.7833L2.38086 6.77222M4.13086 4.14722H18.1309C19.0974 4.14722 19.8809 4.93072 19.8809 5.89722V16.3972C19.8809 17.3637 19.0974 18.1472 18.1309 18.1472H4.13086C3.16436 18.1472 2.38086 17.3637 2.38086 16.3972V5.89722C2.38086 4.93072 3.16436 4.14722 4.13086 4.14722Z"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.1309 15.9863H11.1396M6.75586 1.98633H15.5059C16.4724 1.98633 17.2559 2.76983 17.2559 3.73633V17.7363C17.2559 18.7028 16.4724 19.4863 15.5059 19.4863H6.75586C5.78936 19.4863 5.00586 18.7028 5.00586 17.7363V3.73633C5.00586 2.76983 5.78936 1.98633 6.75586 1.98633Z"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg width="25" height="28" viewBox="0 0 25 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.5584 9.60339H6.82297C5.89707 9.60339 5.14648 10.354 5.14648 11.2799V17.1476C5.14648 18.0735 5.89707 18.8241 6.82297 18.8241H18.5584C19.4843 18.8241 20.2348 18.0735 20.2348 17.1476V11.2799C20.2348 10.354 19.4843 9.60339 18.5584 9.60339Z"
      stroke="black"
      strokeWidth="0.914446"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.49902 9.60351V6.25054C8.49902 5.13896 8.9406 4.07291 9.7266 3.2869C10.5126 2.5009 11.5787 2.05933 12.6902 2.05933C13.8018 2.05933 14.8679 2.5009 15.6539 3.2869C16.4399 4.07291 16.8814 5.13896 16.8814 6.25054V9.60351"
      stroke="black"
      strokeWidth="0.914446"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Ajustes: lista y navegación como antes (/ajustes).
 * Al elegir correo / contraseña / celular se abre la nueva UI en overlay (sin rutas hijas).
 */
export default function Ajustes() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isDataLoaded } = useUser();

  const [overlayScreen, setOverlayScreen] = useState(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    if (isDataLoaded) {
      setLoading(false);
    }
  }, [isDataLoaded]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
    }
  }, [navigate, user, authLoading]);

  const handleLogout = () => {
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    localStorage.removeItem("jwt");
    navigate("/authentication/sign-in");
  };

  const closeOverlay = useCallback(() => {
    setOverlayScreen(null);
  }, []);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "white", padding: "20px", paddingBottom: "100px" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}>
        <IconButton onClick={() => navigate("/perfil")} sx={{ color: "#000", padding: "8px", marginRight: "16px" }}>
          <ArrowBackIcon />
        </IconButton>
        <h1 style={{ fontWeight: "bold", color: "#000", fontSize: "20px", margin: 0 }}>Ajustes</h1>
      </div>

      <div style={{ width: "100%", maxWidth: "400px", margin: "0 auto" }}>
        <div
          onClick={() => setOverlayScreen("email")}
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginRight: "16px", display: "flex", alignItems: "center" }}>
            <EmailIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: "500" }}>Correo electrónico</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: "#666", fontSize: "20px" }} />
        </div>

        <div
          onClick={() => setOverlayScreen("password")}
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginRight: "16px", display: "flex", alignItems: "center" }}>
            <LockIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: "500" }}>Contraseña</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: "#666", fontSize: "20px" }} />
        </div>

        <div
          onClick={() => setOverlayScreen("phone")}
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginRight: "16px", display: "flex", alignItems: "center" }}>
            <PhoneIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: "500" }}>Número de celular</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: "#666", fontSize: "20px" }} />
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "400px", margin: "280px auto 0", textAlign: "center" }}>
        <Typography onClick={handleLogout} sx={{ color: "#d32f2f", fontSize: "16px", fontWeight: "500", cursor: "pointer" }}>
          Cerrar sesión
        </Typography>
      </div>

      {overlayScreen ? (
        <div className="AjustesChangeOverlay" role="presentation">
          <AjustesChangeScreen key={overlayScreen} screen={overlayScreen} embedded onClose={closeOverlay} />
        </div>
      ) : null}
    </div>
  );
}
